import { ColumnDef } from '@tanstack/react-table'
import { PromotionAnalyticsData } from '@/modules/dashboard/type'
import { Button } from '@/common/ui/button'
import { SortingArrows } from '@/common/components/SortingArrows'
import { Badge } from '@/common/ui/badge'
import { Eye } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useDateFilter } from '@/common/hooks/useDateFilter'

export const columns: ColumnDef<PromotionAnalyticsData>[] = [
	{
		accessorKey: 'promotionName',
		header: 'ชื่อโปรโมชั่น',
	},
	{
		id: 'status',
		header: 'ประเภท',
		cell: ({ row }) => {
			const { isForPlayer, isCoupon, isForGuestOnly } = row.original

			if (isForPlayer) {
				return <Badge variant="default">สำหรับผู้เล่น</Badge>
			}
			if (isCoupon) {
				return <Badge variant="secondary">พิเศษ (คูปอง/แคมเปญ)</Badge>
			}
			if (isForGuestOnly) {
				return <Badge variant="default">เฉพาะแขก</Badge>
			}

			return <Badge variant="default">ไม่ระบุ</Badge>
		},
	},

	{
		accessorKey: 'playerCount',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					จำนวนผู้เล่น
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => row.original.playerCount.toLocaleString(),
	},
	{
		accessorKey: 'totalReceiveTime',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					จำนวนรับทั้งหมด / สถานะเทิร์น
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const { totalReceiveTime, totalTimeFinishTurnover } = row.original
			const notPassedTurnover = totalReceiveTime - totalTimeFinishTurnover

			return (
				<div className="flex flex-col">
					<span className="text-sm font-medium">
						{totalReceiveTime.toLocaleString()} ครั้ง (
						{row.original.playerAmountFinishTurnover} ผู้เล่นผ่านเทิร์น)
					</span>
					<div className="mt-1 flex flex-col gap-1">
						<span className="text-xs text-red-500">
							ผ่านเทิร์น: {totalTimeFinishTurnover.toLocaleString()} ครั้ง
						</span>
						<span className="text-xs text-green-500">
							ไม่ผ่านเทิร์น: {notPassedTurnover.toLocaleString()} ครั้ง
						</span>
					</div>
				</div>
			)
		},
	},
	{
		id: 'profit',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					กำไร / เฉลี่ย (บาท)
				</Button>
			)
		},
		cell: ({ row }) => {
			const { totalDepositAmount, totalTransferAmount, totalReceiveTime } =
				row.original
			// profit = totalDepositAmount - totalTransferAmount
			const profit = totalDepositAmount - totalTransferAmount
			const averageProfit = totalReceiveTime > 0 ? profit / totalReceiveTime : 0
			const isPositive = profit >= 0

			return (
				<div className="flex flex-col">
					<span
						className={`text-sm font-medium ${isPositive ? 'text-green-500' : 'text-red-500'}`}
					>
						฿{profit.toLocaleString()}
					</span>
					<span className="text-xs text-muted-foreground">
						เฉลี่ย: ฿
						{averageProfit.toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</span>
				</div>
			)
		},
	},
	{
		accessorKey: 'totalDepositAmount',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					ยอดฝากรวม / เฉลี่ย (บาท)
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const { totalDepositAmount, totalReceiveTime } = row.original
			const average =
				totalReceiveTime > 0 ? totalDepositAmount / totalReceiveTime : 0
			return (
				<div className="flex flex-col">
					<span className="text-sm text-green-500">
						฿{totalDepositAmount.toLocaleString()}
					</span>
					<span className="text-xs text-muted-foreground">
						เฉลี่ย: ฿
						{average.toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</span>
				</div>
			)
		},
	},

	{
		accessorKey: 'balanceAfterTurnoverFinish',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					ยอดคงเหลือหลังทำเทิร์นสำเร็จ / เฉลี่ย (บาท)
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const {
				balanceAfterTurnoverFinish,
				totalReceiveTime,
				totalTimeFinishTurnover,
			} = row.original
			const averagePerReceiveTime =
				totalReceiveTime > 0 ? balanceAfterTurnoverFinish / totalReceiveTime : 0
			const averagePerFinishTime =
				totalTimeFinishTurnover > 0
					? balanceAfterTurnoverFinish / totalTimeFinishTurnover
					: 0

			return (
				<div className="flex flex-col">
					<span className="text-sm font-medium">
						฿{balanceAfterTurnoverFinish.toLocaleString()}
					</span>
					<div className="mt-1 flex flex-col gap-1">
						<span className="text-xs text-gray-500">
							เฉลี่ยทั้งหมด: ฿
							{averagePerReceiveTime.toLocaleString(undefined, {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})}
						</span>
						<span className="text-xs text-gray-500">
							เฉลี่ยหลังทำเทิร์นสำเร็จ: ฿
							{averagePerFinishTime.toLocaleString(undefined, {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})}
						</span>
					</div>
				</div>
			)
		},
	},

	{
		accessorKey: 'totalTransferAmount',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					ยอดโอนกระเป๋าทั้งหมด / เฉลี่ย (บาท)
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const { totalTransferAmount, totalReceiveTime, totalTimeFinishTurnover } =
				row.original
			const averagePerReceiveTime =
				totalReceiveTime > 0 ? totalTransferAmount / totalReceiveTime : 0
			const averagePerFinishTime =
				totalTimeFinishTurnover > 0
					? totalTransferAmount / totalTimeFinishTurnover
					: 0

			return (
				<div className="flex flex-col">
					<span className="text-sm font-medium">
						฿{totalTransferAmount.toLocaleString()}
					</span>
					<div className="mt-1 flex flex-col gap-1">
						<span className="text-xs text-gray-500">
							เฉลี่ยทั้งหมด: ฿
							{averagePerReceiveTime.toLocaleString(undefined, {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})}
						</span>
						<span className="text-xs text-gray-500">
							เฉลี่ยหลังทำเทิร์นสำเร็จ: ฿
							{averagePerFinishTime.toLocaleString(undefined, {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})}
						</span>
					</div>
				</div>
			)
		},
	},

	{
		accessorKey: 'playerTurnoverAmountAfterClearTurnover',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					ยอดเทิร์นที่ทำได้ในกรณีทำเทิร์นไม่สำเร็จ / เฉลี่ย
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const {
				playerTurnoverAmountAfterClearTurnover,
				totalReceiveTime,
				totalTimeFinishTurnover,
			} = row.original
			const averageFailedTurnover =
				totalReceiveTime > 0 && totalTimeFinishTurnover > 0
					? playerTurnoverAmountAfterClearTurnover /
						(totalReceiveTime - totalTimeFinishTurnover)
					: 0
			const averagePerReceiveTime =
				totalReceiveTime > 0
					? playerTurnoverAmountAfterClearTurnover / totalReceiveTime
					: 0

			return (
				<div className="flex flex-col">
					<span className="text-sm font-medium">
						{playerTurnoverAmountAfterClearTurnover.toLocaleString()}
					</span>
					<span className="text-xs text-gray-500">
						เฉลี่ย:{' '}
						{averagePerReceiveTime.toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</span>
					<span className="text-xs text-gray-500">
						เฉลี่ยหลังทำเทิร์นไม่สำเร็จ:{' '}
						{averageFailedTurnover.toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</span>
				</div>
			)
		},
	},

	{
		id: 'actions',
		header: 'การดำเนินการ',
		cell: ({ row }) => {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			const navigate = useNavigate()
			// eslint-disable-next-line react-hooks/rules-of-hooks
			const { dateFilter } = useDateFilter()
			const { startDate, endDate } = dateFilter

			return (
				<Button
					variant="ghost"
					size="sm"
					onClick={() =>
						navigate(
							`/operator/analytics/promotion/detail/${row.original.promotionId}${startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ''}`,
						)
					}
					className="flex items-center gap-2"
				>
					<Eye size={16} />
					ดูรายละเอียด
				</Button>
			)
		},
	},
]
